import {Button, Card, Col, Drawer, message, Row, Space, Spin, Table, Tag} from "antd";
import {useEffect, useRef, useState} from "react";
import {EditOutlined, ExclamationCircleOutlined, EyeOutlined} from "@ant-design/icons";
import {
    COMPONENT_TYPE,
    NOT_AVAILABLE,
    RECONCILE_TYPES,
    RECONCILIATION_STATUS,
    YYYY_MM_DD
} from "../../../util/Constants";
import TableSearchComp from '../../common/tableSearch/TableSearchComp';
import {ReconciliationTableDataDto} from "../../../dto/admin/ReconciliationTableDataDto";
import {AccountWiseFundDetailDto} from "../../../dto/AccountWiseFundDetailDto";
import CustomerInvestRequestTableComp from "./customerInvestRequestTableComp";
import transactionService from "../../../service/transactionService";
import {
    MSG_FAILED_TO_FETCH_AUTO_RECONCILED_LIST_ERROR,
    MSG_FAILED_TO_FETCH_PENDING_TRANSACTION_LIST_ERROR,
    MSG_MANUAL_RECONCILE_FAILURE,
    MSG_MANUAL_RECONCILE_SUCCESS,
    MSG_SELECT_RECORDS_TO_PROCEED
} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";
import {ManualReconciliationDto} from "../../../dto/admin/ManualReconciliationDto";
import modal from "antd/es/modal";
import transactionFetchService from "../../../service/transactionFetchService";
import dayjs from "dayjs";
import {toNumber} from "lodash";


interface ReconciliationTableCompProps {
    spinnerLoad: (spin: boolean) => void
    notReconcileList?: ReconciliationTableDataDto[]
    tableType: RECONCILE_TYPES
    reconcileGroupNo: number
    isCanColumnHide?: boolean
}

const ReconciliationTableComp = (props: ReconciliationTableCompProps) => {

    const [autoReconcileList, setAutoReconcileList] = useState<ReconciliationTableDataDto[]>([]);
    const [manualReconcileList, setManualReconcileList] = useState<ReconciliationTableDataDto[]>([]);
    const [selectedRecordList, setSelectedRecordList] = useState<ReconciliationTableDataDto[]>([]);
    const [autoReconciledCustomerRecordList, setAutoReconciledCustomerRecordList] = useState<AccountWiseFundDetailDto[]>([]);
    const [openAutoReconDrawer, setAutoReconDrawerOpen] = useState(false);
    const [openPendingTransDrawer, setPendingTransOpen] = useState(false);
    const [isReconcileTableColumnHide, setReconcileTableColumnHide] = useState(false);
    const [isSpinning, setSpinning] = useState(false);
    const [reconcileGroupNo, setReconcileGroupNo] = useState<number>(0);
    const childRef = useRef();

    useEffect(() => {
        let groupNo;
        if (0 == props.reconcileGroupNo) {
            groupNo = localStorage.getItem("reconcileGroupNo");
            setReconcileGroupNo(toNumber(groupNo))
        } else {
            groupNo = props.reconcileGroupNo
            setReconcileGroupNo(groupNo)
        }

        if (RECONCILE_TYPES.AUTO === props.tableType) {
            fetchAutoReconciledBankDetailsList(groupNo as number)
        } else {
            if (undefined != props.notReconcileList) {
                setManualReconcileList(props.notReconcileList)
            } else {
                fetchPendingTransactionList(groupNo as number)
            }
        }

        if (undefined != props.isCanColumnHide) {
            setReconcileTableColumnHide(props.isCanColumnHide)
        }

    }, []);


    const reconciliationColumns = [

        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            sorter: (a: any, b: any) => {
                a = a.date == null ? '' : a.date
                b = b.date == null ? '' : b.date
                return a.localeCompare(b);
            },
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Portfolio ID',
            dataIndex: 'portFolioId',
            sorter: (a: any, b: any) => {
                a = a.portFolioId == null ? '' : a.portFolioId
                b = b.portFolioId == null ? '' : b.portFolioId
                return a.localeCompare(b);
            },
            ...TableSearchComp('portFolioId', 'Portfolio ID'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Fund',
            dataIndex: 'fund',
            sorter: (a: any, b: any) => {
                a = a.fund == null ? '' : a.fund
                b = b.fund == null ? '' : b.fund
                return a.localeCompare(b);
            },
            ...TableSearchComp('fund', 'Fund'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Amount',
            dataIndex: 'value',
            align: 'right',
            sorter: (a: any, b: any) => a.value - b.value,
            ...TableSearchComp('value', 'Amount'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{toFixed(text)}</span>)
            }

        },
        {
            title: 'Collection Bank',
            dataIndex: 'collectionBank',
            sorter: (a: any, b: any) => {
                a = a.collectionBank == null ? '' : a.collectionBank
                b = b.collectionBank == null ? '' : b.collectionBank
                return a.localeCompare(b);
            },
            ...TableSearchComp('collectionBank', 'Collection Bank'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Ref No.',
            dataIndex: 'nic',
            sorter: (a: any, b: any) => {
                a = a.nic == null ? '' : a.nic
                b = b.nic == null ? '' : b.nic
                return a.localeCompare(b);
            },
            ...TableSearchComp('nic', 'NIC No.'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Reconciliation Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center',
            hidden: RECONCILE_TYPES.AUTO === props.tableType || isReconcileTableColumnHide,
            sorter: (a: any, b: any) => {
                a = a.status == null ? '' : a.status
                b = b.status == null ? '' : b.status
                return a.localeCompare(b);
            },
            render: (text: any, rec: any) => {
                return getTag(text)
            }
        },
        {
            title: 'Reconciled Cust Record ID',
            dataIndex: 'autoReconciledCustomerDetailId',
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            },
            sorter: (a: any, b: any) => a.autoReconciledCustomerDetailId - b.autoReconciledCustomerDetailId,
            hidden: RECONCILE_TYPES.AUTO !== props.tableType || isReconcileTableColumnHide,
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            align: 'center',
            fixed: 'right' as const,
            hidden: isReconcileTableColumnHide,
            render: (text: any, rec: any) => (
                <Space size="middle">
                    {RECONCILE_TYPES.AUTO == props.tableType ?
                        <Button className='btn-action-outline-common btn-action-view'
                                onClick={() => showAutoReconDrawer(rec)}
                                icon={<EyeOutlined/>}>View</Button>
                        :
                        <Button className='btn-action-outline-common btn-action-view'
                                disabled={RECONCILIATION_STATUS.MANUAL_RECONCILE === rec.status}
                                onClick={() => showPendingTransReconDrawer(rec)}
                                icon={<EditOutlined/>}>Edit</Button>}

                </Space>
            ),
        },
    ].filter(item => !item.hidden);


    const getTag = (status: string) => {
        switch (status) {
            case RECONCILIATION_STATUS.AUTO_RECONCILE: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#7cb305' key={status}>
                        {status.toUpperCase().replaceAll('_', ' ')}
                    </Tag>
                )
            }
            case RECONCILIATION_STATUS.MANUAL_RECONCILE: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#1d39c4' key={status}>
                        {status.toUpperCase().replaceAll('_', ' ')}
                    </Tag>
                )
            }
            case RECONCILIATION_STATUS.REJECT: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#fa541c' key={status}>
                        {status.toUpperCase().replaceAll('_', ' ')}
                    </Tag>
                )
            }
            case RECONCILIATION_STATUS.RECONCILE_INPROGRESS: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#d4b106' key={status}>
                        {status.toUpperCase().replaceAll('_', ' ')}
                    </Tag>
                )
            }
            case RECONCILIATION_STATUS.SUSPEND: {
                return (
                    <Tag className={'drl-tbl-tag'}
                         color='#8c8c8c' key={status}>
                        {status.toUpperCase().replaceAll('_', ' ')}
                    </Tag>
                )
            }
        }
    }
    const showAutoReconDrawer = (record: any) => {
        setSelectedRecordList([record])
        setAutoReconciledCustomerRecordList(record.autoReconciledCustomerDetails)
        setAutoReconDrawerOpen(true);
        setReconcileTableColumnHide(true)

    };


    const onCloseAutoReconDrawer = () => {
        setAutoReconDrawerOpen(false);
        setReconcileTableColumnHide(false)
    };


    const toFixed = (value: string | undefined): any => {
        if (undefined != value) {
            let val = value.toString();
            if (val.indexOf(".") != -1) {
                val = val.slice(0, (val.indexOf(".")) + 3);
            }
            val = (+val).toFixed(2);
            return val.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
        return "0.00";
    }

    const onUpdate = (spin: boolean) => {
        setSpinning(spin)
    }

    const showPendingTransReconDrawer = (record: any) => {
        setSelectedRecordList([record])
        setPendingTransOpen(true);
        setReconcileTableColumnHide(true)
    };

    const onClosePendingTransDrawer = () => {
        setPendingTransOpen(false);
        setReconcileTableColumnHide(false)
    };

    const onManualReconcile = () => {

        let selectedIdList: number[] = (childRef?.current as any)?.getSelectedIdList();
        if (selectedIdList && selectedIdList.length > 0) {
            modal.confirm({
                title: 'Confirm',
                icon: <ExclamationCircleOutlined/>,
                content: `Are you sure?, Do you want to manually reconcile?`,
                okText: 'Yes',
                cancelText: 'No',
                okButtonProps: {style: {background: '#FF7400'}},
                onOk() {
                    setSpinning(true)
                    let manualReconciliationDto = {
                        bankExcelDetailId: selectedRecordList[0].id,
                        accountWiseFundDetailIds: selectedIdList
                    } as ManualReconciliationDto

                    transactionService.manualReconciliation(manualReconciliationDto).then((res) => {
                        if (res.data.results) {
                            onClosePendingTransDrawer()
                            message.success(MSG_MANUAL_RECONCILE_SUCCESS)
                            fetchPendingTransactionList(reconcileGroupNo)
                            setSpinning(false)
                        } else {
                            message.error(MSG_MANUAL_RECONCILE_FAILURE)
                            setSpinning(false)
                        }
                    }, (reason) => {
                        ErrorHandler.handleError(reason);
                        setSpinning(false)

                    })
                },
                onCancel() {

                }
            });
        } else {
            message.warning(MSG_SELECT_RECORDS_TO_PROCEED)
        }
    }
    const fetchPendingTransactionList = (reconcileGroupNo: number) => {
        props.spinnerLoad(true)
        transactionFetchService.getReconciliationPendingAndManualReconciledBankDetails(reconcileGroupNo).then(res => {
            if (res.data !== null) {
                setManualReconcileList(res.data.results)
                props.spinnerLoad(false)
            } else {
                message.error(MSG_FAILED_TO_FETCH_PENDING_TRANSACTION_LIST_ERROR).then();
                props.spinnerLoad(false)
            }

        }, (reason) => {
            ErrorHandler.handleError(reason);
            props.spinnerLoad(false)

        })
    }

    const fetchAutoReconciledBankDetailsList = (reconcileGroupNo: number) => {
        props.spinnerLoad(true)
        transactionFetchService.getAutoReconciledBankDetails(reconcileGroupNo).then(res => {
            if (res.data !== null) {
                setAutoReconcileList(res.data.results)
                props.spinnerLoad(false)

            } else {
                message.error(MSG_FAILED_TO_FETCH_AUTO_RECONCILED_LIST_ERROR).then();
                props.spinnerLoad(false)
            }

        }, (reason) => {
            ErrorHandler.handleError(reason);
            props.spinnerLoad(false)
        })
    }
    return (
        <>
            <Table scroll={{x: 'max-content'}} columns={reconciliationColumns}
                   dataSource={RECONCILE_TYPES.AUTO === props.tableType ? autoReconcileList : manualReconcileList}/>

            <Drawer className={'recon-drawer recon-drawer-custom-width'} size={'large'}

                    title={<><span>Reconciliation Details</span> <Tag className={'drl-tbl-tag'} color='#7cb305'>AUTO RECONCILED</Tag></>}
                    destroyOnClose={true}
                    onClose={onCloseAutoReconDrawer} open={openAutoReconDrawer}>
                <Spin spinning={isSpinning}>
                    <Row gutter={[16, 20]}>
                        <Col span={24}>
                            <Card size={'small'} title={'Bank Record'}>
                                <Table scroll={{x: 'max-content'}} size="small" columns={reconciliationColumns}
                                       dataSource={selectedRecordList}
                                       pagination={{position: ['none', 'none']}}></Table>
                            </Card>
                        </Col>
                        <CustomerInvestRequestTableComp cardSize={"small"} dataSource={autoReconciledCustomerRecordList}
                                                        spinnerLoad={onUpdate} compType={COMPONENT_TYPE.VIEW}
                                                        isCanShowButton={false}
                                                        isCanShowRowSelection={false}/>
                    </Row>
                </Spin>
            </Drawer>

            <Drawer className={'recon-drawer recon-drawer-custom-width custom-invest-add-btn-size'} size={'large'}
                    title={<span>Reconciliation Details</span>}
                    destroyOnClose={true}
                    onClose={onClosePendingTransDrawer} open={openPendingTransDrawer}>
                <Spin spinning={isSpinning}>
                    <Row gutter={[16, 20]}>
                        <Col span={24}>
                            <Card size={'small'} title={'Bank Record'}>
                                <Table scroll={{x: 'max-content'}} size="small" columns={reconciliationColumns}
                                       dataSource={selectedRecordList}
                                       pagination={{position: ['none', 'none']}}></Table>
                            </Card>
                        </Col>
                        <CustomerInvestRequestTableComp ref={childRef} cardSize={"small"} spinnerLoad={onUpdate}
                                                        compType={COMPONENT_TYPE.ACTION} isCanShowRowSelection={true}
                                                        isCanShowButton={true} bankRecord={selectedRecordList}/>
                        <Col span={24} className='drawer-manual-recon-btn'>
                            <Button type='primary' onClick={onManualReconcile}>Manual Reconcile</Button>
                        </Col>
                    </Row>
                </Spin>
            </Drawer>
        </>
    )
}
export default ReconciliationTableComp;